import { useCallback, useEffect, useRef, useState } from "react";
import { FetchError } from "lib/actions/fetcher";
import { beginAutodeskAuth } from "lib/actions/autodesk/beginAutodeskAuth";

export const AUTODESK_SESSION_STORAGE_KEY = "autodeskAuthSessionId";

export enum AutodeskConnectStatus {
  Idle,
  Starting,
  Authenticating,
  Complete,
  Error,
}

/**
 * The consent popup lifecycle.
 *
 * Simpler than the Speckle equivalent because APS keeps the PKCE verifier and
 * the expected `state` server-side: the only thing stored here is a session id
 * for the callback page to quote back.
 */
export function useAutodeskConnect(
  workspaceId: number,
  onConnected: () => void,
) {
  const [status, setStatus] = useState(AutodeskConnectStatus.Idle);
  const [error, setError] = useState<string | null>(null);
  const [popupUrl, setPopupUrl] = useState<string | null>(null);

  // Read inside the listener, which is registered once per popup and would
  // otherwise close over the callback as it was when the popup opened.
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const connect = useCallback(async () => {
    setError(null);
    setStatus(AutodeskConnectStatus.Starting);

    try {
      const session = await beginAutodeskAuth(workspaceId);
      localStorage.setItem(AUTODESK_SESSION_STORAGE_KEY, session.sessionId);
      setPopupUrl(session.url);
      setStatus(AutodeskConnectStatus.Authenticating);
    } catch (thrown) {
      console.error(thrown);
      setError(describeBeginError(thrown));
      setStatus(AutodeskConnectStatus.Error);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (status !== AutodeskConnectStatus.Authenticating || !popupUrl) return;

    const popup = globalThis.open(
      popupUrl,
      "autodeskAuth",
      "width=800,height=800",
    );
    if (!popup) {
      setError(
        "Your browser blocked the Autodesk sign-in window. Allow pop-ups for " +
          "this site and try again.",
      );
      setStatus(AutodeskConnectStatus.Error);
      return;
    }
    popup.focus();

    function onMessage(
      event: MessageEvent<{ type?: string; error?: boolean; message?: string }>,
    ) {
      if (!event.data || event.data.type !== "autodesk-authentication") return;

      // The popup is our own callback page, so anything claiming to be it from
      // another origin is not it.
      if (event.origin !== globalThis.location.origin) return;

      if (event.data.error) {
        setError(
          event.data.message ??
            "The Autodesk account could not be connected. Please try again.",
        );
        setStatus(AutodeskConnectStatus.Error);
        return;
      }

      setStatus(AutodeskConnectStatus.Complete);
      onConnectedRef.current();
    }

    const closedCheck = setInterval(() => {
      if (!popup.closed) return;
      clearInterval(closedCheck);
      // Only meaningful while still authenticating. The popup closing after a
      // successful exchange is the normal end of the flow, not a failure.
      setStatus((current) =>
        current === AutodeskConnectStatus.Authenticating
          ? AutodeskConnectStatus.Idle
          : current,
      );
    }, 1000);

    globalThis.addEventListener("message", onMessage, false);
    return () => {
      clearInterval(closedCheck);
      globalThis.removeEventListener("message", onMessage);
    };
  }, [status, popupUrl]);

  return { connect, status, error };
}

/**
 * The three ways beginning a connection fails all have different fixes, so
 * they are not collapsed into one message.
 */
function describeBeginError(thrown: unknown): string {
  if (thrown instanceof FetchError) {
    if (thrown.status === 403)
      return (
        "Connectors are part of the Business plan. Upgrade the workspace to " +
        "connect Autodesk accounts."
      );
    if (thrown.status === 409)
      return (
        "This workspace has no enabled Autodesk app registration. A workspace " +
        "admin has to add one, or turn the existing one back on, before " +
        "accounts can be connected."
      );
  }
  return "The Autodesk sign-in could not be started. Please try again.";
}
